// crewbit-admin/src/components/money/settings/TaxesPanel.tsx
//
// Taxes group sub-screen body. Per spec §Settings.7.1 + §3c LOCKED:
//   - HstNumberEditor (CRA-format validated, inline-save).
//   - Tax_mode editor: 3-mode selector. Switching to 'quick_method' opens
//     TaxModeWizard (3-step modal). Other transitions are 1-tap.
//   - MileageRatesEditor (rate-1 / rate-2 / threshold, inline-save).
//   - Tax destination deep-link (routes to /money/tax for filing flow).
//
// fiscal_year_start intentionally OMITTED (queued substrate fix).
// Quick-method rate/credit OMITTED — wizard commits tax_mode only;
// rate is auto-derived in world-class quick-method automation track.
//
// Mirrors crewbit-mobile/components/money/settings/TaxesPanel.tsx.

import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight } from 'lucide-react';
import { palette, radii, spacing, typography } from '../../../styles/tokens';
import { Skeleton } from '../../common/Skeleton';
import {
  useBusinessFinancialSettings,
  type TaxMode,
} from '../../../hooks/useBusinessFinancialSettings';
import { useUpdateBusinessMoneySettings } from '../../../hooks/useUpdateBusinessMoneySettings';
import { useDeriveQuickMethodConfig } from '../../../hooks/useDeriveQuickMethodConfig';
import { lookupErrorCopy } from '../../../lib/errorMap';
import { Toast } from '../../common/Toast';
import HstNumberEditor, { type HstSaveResult } from './HstNumberEditor';
import MileageRatesEditor, { type MileageSaveResult } from './MileageRatesEditor';
import TaxModeWizard from './TaxModeWizard';
import type { DeriveQuickMethodConfigSuccess } from '../../../types/quickMethod';

type QuickMethodConfigData = Omit<DeriveQuickMethodConfigSuccess, 'success'>;

const TAX_MODES: { mode: TaxMode; label: string; description: string }[] = [
  {
    mode: 'charge',
    label: 'Charge HST/GST',
    description: 'Standard mode — collect tax from customers, remit input tax credits to CRA.',
  },
  {
    mode: 'quick_method',
    label: 'Quick Method',
    description: 'Simplified remittance — collect full tax, remit a fixed percentage. Eligible if revenue < $400k.',
  },
  {
    mode: 'exempt',
    label: 'Exempt / Small supplier',
    description: 'Not registered for HST/GST — no tax collection or remittance.',
  },
];

interface TaxesPanelProps {
  businessId: string | null;
}

export default function TaxesPanel({ businessId }: TaxesPanelProps) {
  const settings = useBusinessFinancialSettings(businessId);
  const updateBiz = useUpdateBusinessMoneySettings();
  const deriveQM = useDeriveQuickMethodConfig();
  const [wizardOpen, setWizardOpen] = useState(false);
  // Tier 2-UI Phase C — derive_quick_method_config envelope state for wizard
  const [qmConfig, setQmConfig] = useState<QuickMethodConfigData | null>(null);
  const [qmLoading, setQmLoading] = useState(false);
  const [qmError, setQmError] = useState<string | null>(null);
  const [qmIsQuebec, setQmIsQuebec] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);

  const data = settings.data;

  const onSaveHst = useCallback(
    async (next: string | null): Promise<HstSaveResult> => {
      if (!businessId) return { success: false, message: 'No active business' };
      const result = await updateBiz({ businessId, hstNumber: next });
      if (result.success) return { success: true };
      return {
        success: false,
        message: lookupErrorCopy('update_business_money_settings', result.error.code),
      };
    },
    [businessId, updateBiz],
  );

  const onSaveMileage = useCallback(
    async (next: {
      mileageRate1: number | null;
      mileageRate2: number | null;
      mileageThreshold: number | null;
    }): Promise<MileageSaveResult> => {
      if (!businessId) return { success: false, message: 'No active business' };
      const result = await updateBiz({ businessId, ...next });
      if (result.success) return { success: true };
      return {
        success: false,
        message: lookupErrorCopy('update_business_money_settings', result.error.code),
      };
    },
    [businessId, updateBiz],
  );

  const handleTaxModeTap = async (next: TaxMode) => {
    if (!businessId || !data) return;
    if (next === data.taxMode) return;
    if (next === 'quick_method') {
      // Phase C: load derived QM config before/while wizard renders.
      setQmLoading(true);
      setQmError(null);
      setQmConfig(null);
      setQmIsQuebec(false);
      setWizardOpen(true);
      const result = await deriveQM({ businessId });
      setQmLoading(false);
      if (result.success) {
        setQmConfig(result.data);
      } else {
        const isQuebec = result.error.code === 'QUEBEC_QM_NOT_SUPPORTED_V1';
        setQmIsQuebec(isQuebec);
        setQmError(lookupErrorCopy('derive_quick_method_config', result.error.code));
      }
      return;
    }
    const result = await updateBiz({ businessId, taxMode: next });
    if (!result.success) {
      setToast({
        message: lookupErrorCopy('update_business_money_settings', result.error.code),
        type: 'warning',
      });
    }
  };

  const handleWizardConfirm = async (args: {
    taxMode: 'quick_method';
    rateOverride: number | null;
  }) => {
    if (!businessId) return { success: false, message: 'No active business' };
    const result = await updateBiz({
      businessId,
      taxMode: args.taxMode,
      quickMethodRemittanceRateOverride: args.rateOverride,
    });
    if (result.success) return { success: true };
    return {
      success: false,
      message: lookupErrorCopy('update_business_money_settings', result.error.code),
    };
  };

  if (settings.isLoading && !data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <Skeleton height={120} />
        <Skeleton height={200} />
        <Skeleton height={160} />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{
        padding: spacing.xl, textAlign: 'center',
        fontSize: typography.size.caption, color: palette.g500,
      }}>
        Pick an active business to manage tax settings.
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        {/* HST */}
        <Card>
          <HstNumberEditor
            label="HST/GST number"
            description="9-digit BN followed by RT0001 (e.g. 123456789RT0001). Used on invoices and tax filings."
            invalidFormatLabel="Format must be 9 digits + RT + 4 digits."
            value={data.hstNumber}
            onSave={onSaveHst}
          />
        </Card>

        {/* Tax mode */}
        <Card>
          <div style={{
            paddingInline: spacing.md, paddingTop: spacing.md, paddingBottom: spacing.sm,
            display: 'flex', flexDirection: 'column', gap: spacing.xxs,
          }}>
            <div style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
              Tax mode
            </div>
            <div style={{ fontSize: typography.size.caption, color: palette.g500 }}>
              How you collect and remit HST/GST. Switching to Quick Method opens a guided wizard.
            </div>
          </div>
          <div>
            {TAX_MODES.map(({ mode, label, description }, idx) => {
              const isLast = idx === TAX_MODES.length - 1;
              const isActive = data.taxMode === mode;
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  onClick={() => { void handleTaxModeTap(mode); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: spacing.sm,
                    paddingInline: spacing.md, paddingBlock: spacing.md,
                    width: '100%', border: 'none', background: 'transparent',
                    textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                    borderBottom: isLast ? 'none' : `1px solid ${palette.g150}`,
                  }}
                >
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.xxs }}>
                    <span style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
                      {label}
                    </span>
                    <span style={{ fontSize: typography.size.caption, color: palette.g500, lineHeight: 1.4 }}>
                      {description}
                    </span>
                  </div>
                  {isActive && <Check size={18} color={palette.accent} strokeWidth={2.5} />}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Mileage */}
        <Card>
          <MileageRatesEditor
            label="Mileage rates"
            description="Per-km rates used to calculate vehicle expense deductions on T2125."
            craReferenceCopy="CRA reference for 2025: $0.70/km for first 5,000 km, $0.64/km thereafter."
            rate1Label="Rate 1 ($/km)"
            rate2Label="Rate 2 ($/km)"
            thresholdLabel="Threshold (km)"
            invalidRateLabel="Rate must be between 0 and 1.00."
            invalidThresholdLabel="Threshold must be greater than 0."
            rate1={data.mileageRate1}
            rate2={data.mileageRate2}
            threshold={data.mileageThreshold}
            onSave={onSaveMileage}
          />
        </Card>

        {/* Tax destination deep-link */}
        <Link
          to="/money/tax"
          style={{
            display: 'flex', alignItems: 'center', gap: spacing.sm,
            background: palette.white, borderRadius: radii.lg,
            border: `1px solid ${palette.g150}`,
            padding: spacing.md, textDecoration: 'none',
          }}
        >
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: spacing.xxs }}>
            <span style={{ fontSize: typography.size.subheading, fontWeight: 600, color: palette.g800 }}>
              Tax filing
            </span>
            <span style={{ fontSize: typography.size.caption, color: palette.g500 }}>
              Generate the tax summary, mark filed, review remittance log.
            </span>
          </div>
          <ChevronRight size={18} color={palette.g400} />
        </Link>
      </div>

      <TaxModeWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onConfirm={handleWizardConfirm}
        quickMethodConfig={qmConfig}
        configLoading={qmLoading}
        configError={qmError}
        isQuebecBlocked={qmIsQuebec}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={true}
          onDismiss={() => setToast(null)}
        />
      )}
    </>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: palette.white,
      borderRadius: radii.lg,
      border: `1px solid ${palette.g150}`,
    }}>
      {children}
    </div>
  );
}
